import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSession } from "../../context/SessionContext";
import { useApi } from "../../hooks/useApi";
import { appealsService, GenerateAppealResponse } from "../../services/appeals";
import { Surgery } from "../../types/patient.types";
import {
  FundamentoKey,
  HeaderRecurso,
  ItemTipo,
  NegativaFonte,
  OpmeBundle,
  ProcedimentoBundle,
  RecursosTextos,
} from "../../types/resources.types";
import { surgeryToPedido } from "../../lib/mappers";
import { generateFundTecTexto } from "./technicalBasis";
import { referenceUrl } from "../references";
import { appealSummary, blockReason, buildAppealPayload, EMPTY_TEXTS, GROUND_KEYS, itemsFromSurgery, STEP_KEYS } from "./appeal";

type References = { procedimentos?: ProcedimentoBundle[]; opmes?: OpmeBundle[] };

/** Estado do assistente de recurso (7 passos), do pedido escolhido até o PDF. */
export function useAppealWizard() {
  const { metadata, setMetadata } = useSession();
  // "Gerar recurso" em Pedidos salvos chega com o pedido escolhido e pula o passo 1.
  const preselected = (useLocation().state as { preselectedSurgery?: Surgery } | null)?.preselectedSurgery;

  const [stepIndex, setStepIndex] = useState(preselected ? 1 : 0);
  const [surgery, setSurgery] = useState<Surgery | null>(preselected ?? null);
  const [itemIds, setItemIds] = useState<string[]>([]);
  const [denialSource, setDenialSource] = useState<NegativaFonte>("texto");
  const [denialText, setDenialText] = useState("");
  const [denialFile, setDenialFile] = useState<File | null>(null);
  const [grounds, setGrounds] = useState<FundamentoKey[]>([]);
  const [texts, setTexts] = useState<RecursosTextos>(EMPTY_TEXTS);
  const [procedimentos, setProcedimentos] = useState<ProcedimentoBundle[]>([]);
  const [opmes, setOpmes] = useState<OpmeBundle[]>([]);
  const [selectedRefs, setSelectedRefs] = useState<Record<number, boolean>>({});
  const [openedUrls, setOpenedUrls] = useState<Set<string>>(new Set());
  const [declared, setDeclared] = useState(false);
  const [header, setHeader] = useState<HeaderRecurso>({ id: "", timestamp_recurso: "", timestamp_validacao: "", medico: metadata.nome, resumo: "" });
  const [reloading, setReloading] = useState<ItemTipo | null>(null);

  const generateApi = useApi<GenerateAppealResponse>();
  const refsApi = useApi<{ referencias: References }>();

  const step = STEP_KEYS[stepIndex];
  const order = useMemo(() => (surgery ? surgeryToPedido(surgery) : null), [surgery]);
  const items = useMemo(() => (surgery ? itemsFromSurgery(surgery) : []), [surgery]);
  const selectedItems = items.filter((i) => itemIds.includes(i.id));
  // Sempre na ordem da lista, não na ordem em que foram marcados.
  const activeGrounds = GROUND_KEYS.filter((g) => grounds.includes(g));

  const selectedRefList = [...procedimentos, ...opmes].flatMap((b) => b.referencias).filter((r) => r && selectedRefs[r.id]);
  const refLinks = selectedRefList.map(referenceUrl).filter((u): u is string => !!u);
  const hasRefs = procedimentos.length > 0 || opmes.length > 0;

  const blocked = blockReason(step, {
    order,
    items: selectedItems.length,
    grounds: activeGrounds.length,
    hasRefs,
    selectedRefs: selectedRefList.length,
    linksOpened: refLinks.every((u) => openedUrls.has(u)),
    declared,
  });

  const toggle = <T,>(list: T[], value: T) => (list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

  const selectSurgery = (next: Surgery) => {
    if (next.timestamp !== surgery?.timestamp) setItemIds([]);
    setSurgery(next);
  };

  // Atualiza só o tipo que veio (recarregar uma seção não apaga a outra); referências novas entram incluídas.
  const hydrate = (refs: References) => {
    if (refs.procedimentos) setProcedimentos(refs.procedimentos);
    if (refs.opmes) setOpmes(refs.opmes);
    const fresh = [...(refs.procedimentos ?? []), ...(refs.opmes ?? [])].flatMap((b) => b.referencias);
    setSelectedRefs((prev) => ({ ...prev, ...Object.fromEntries(fresh.map((r) => [r.id, true])) }));
  };

  const payload = (kind?: ItemTipo) =>
    buildAppealPayload({ grounds: kind ? undefined : activeGrounds, denialSource, denialText, items: selectedItems, doctorName: metadata.nome, email: metadata.email, kind });
  const file = denialSource === "arquivo" ? denialFile : null;

  const generate = async () => {
    const data = await generateApi.request(() => appealsService.generate(generateApi.apiFetchForm, payload(), file));
    if (!data) return;
    hydrate(data.referencias);
    // Ao gerar de novo, o que o médico já editou fica.
    setTexts((prev) => ({
      identificacaoNegativa: data.negativa ?? "",
      fundTec: generateFundTecTexto(data.referencias.procedimentos ?? [], data.referencias.opmes ?? []),
      cdc: prev.cdc || (data.textos?.cdc ?? ""),
      atoMedico: prev.atoMedico || (data.textos?.atoMedico ?? ""),
      lgpd: prev.lgpd || (data.textos?.lgpd ?? ""),
      resolucao: prev.resolucao || (data.textos?.resolucao ?? ""),
    }));
    setHeader((prev) => ({ ...prev, id: `REC-${crypto.randomUUID()}`, timestamp_recurso: new Date().toISOString(), resumo: appealSummary(activeGrounds) }));
    setMetadata((prev) => ({ ...prev, total_recursos: prev.total_recursos + 1 }));
    setStepIndex(STEP_KEYS.indexOf("generated"));
  };

  const reloadReferences = async (kind: ItemTipo) => {
    setReloading(kind);
    const data = await refsApi.request(() => appealsService.references(refsApi.apiFetchForm, payload(kind), file));
    setReloading(null);
    if (!data) return;
    hydrate(kind === "CODIGO" ? { procedimentos: data.referencias.procedimentos ?? [] } : { opmes: data.referencias.opmes ?? [] });
  };

  const goNext = () => {
    if (blocked) return;
    if (step === "confirm") return void generate();
    setStepIndex((i) => Math.min(i + 1, STEP_KEYS.length - 1));
  };
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  const toggleDeclared = () => {
    setHeader((prev) => ({ ...prev, timestamp_validacao: new Date().toISOString() }));
    setDeclared((prev) => !prev);
  };

  const reset = () => {
    setStepIndex(0);
    setSurgery(null);
    setItemIds([]);
    setDenialSource("texto");
    setDenialText("");
    setDenialFile(null);
    setGrounds([]);
    setTexts(EMPTY_TEXTS);
    setProcedimentos([]);
    setOpmes([]);
    setSelectedRefs({});
    setOpenedUrls(new Set());
    setDeclared(false);
  };

  return {
    step,
    stepIndex,
    blocked,
    goNext,
    goBack,
    reset,
    generating: generateApi.loading,

    surgery,
    order,
    selectSurgery,

    items,
    itemIds,
    selectedItems,
    toggleItem: (id: string) => setItemIds((prev) => toggle(prev, id)),
    denialSource,
    setDenialSource,
    denialText,
    setDenialText,
    denialFile,
    setDenialFile,

    activeGrounds,
    toggleGround: (g: FundamentoKey) => setGrounds((prev) => toggle(prev, g)),

    texts,
    updateText: (partial: Partial<RecursosTextos>) => setTexts((prev) => ({ ...prev, ...partial })),

    procedimentos,
    opmes,
    hasRefs,
    selectedRefs,
    selectedRefList,
    toggleRef: (id: number) => setSelectedRefs((prev) => ({ ...prev, [id]: !prev[id] })),
    openedUrls,
    markOpened: (url: string) => setOpenedUrls((prev) => new Set(prev).add(url)),
    linkCount: refLinks.length,
    openedCount: refLinks.filter((u) => openedUrls.has(u)).length,
    reloading,
    reloadReferences,

    declared,
    toggleDeclared,
    header,
  };
}

export type AppealWizard = ReturnType<typeof useAppealWizard>;
